import styled, { css } from "styled-components";

import { buttonLoadingCss } from "../../../components/Button";
import { Skeleton as BaseSkeleton } from "../../../components/Skeleton";
import { fireRingWrapper } from "../../../styles/fireRing";
import { MOBILE } from "./mobile";

export const Container = styled.div`
  padding-bottom: 3rem;

  &,
  & * {
    font-family: Inter, sans-serif;
  }
`;

/* A titled block of the screen: small uppercase label + its content (same as /account and /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 760px;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }

  /* The page's title, spaced like "Meus Keepos" on /sites */
  &.title {
    padding-top: 2rem;

    @media ${MOBILE} {
      padding-top: 20px;
    }
  }
`;

export const Tag = styled.span<{ $tone?: "plus" | "warning" | "danger" | "neutral" }>`
  flex-shrink: 0;
  padding: 0.125rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 800;
  border-radius: 100px;
  letter-spacing: 0.02em;

  /* The search's seals (OFF, 5 years, popular): smaller, so they don't compete with the name */
  &.seal {
    padding: 0.0625rem 0.375rem;
    font-size: 0.5625rem;
    line-height: 0.875rem;
    letter-spacing: 0.03em;
  }

  ${({ $tone = "neutral" }) =>
    ({
      plus: css`
        color: #0d3d1c;
        background: var(--color-brand-plus);
      `,
      warning: css`
        color: #ffb066;
        background: rgba(255, 122, 0, 0.16);
      `,
      danger: css`
        color: #ff8aa0;
        background: rgba(255, 66, 100, 0.16);
      `,
      neutral: css`
        color: var(--color-neutral-white);
        background: var(--color-neutral-base);
      `,
    })[$tone]}
`;

/* Small pill buttons: the main one filled, the others outlined */
export const SmallButton = styled.button<{ $primary?: boolean; $danger?: boolean; $loading?: boolean }>`
  flex-shrink: 0;
  padding: 0.5rem 0.875rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ $danger }) => ($danger ? "var(--color-feedback-danger)" : "var(--color-neutral-white)")};
  white-space: nowrap;
  background: ${({ $primary }) => ($primary ? "var(--color-brand-blue)" : "transparent")};
  border: 1px solid ${({ $primary }) => ($primary ? "var(--color-brand-blue)" : "var(--color-neutral-base)")};
  border-radius: 100px;
  transition:
    border-color 0.15s,
    filter 0.15s;

  &:hover:not(:disabled) {
    filter: brightness(1.12);
    border-color: ${({ $primary, $danger }) =>
      $danger ? "var(--color-feedback-danger)" : $primary ? "var(--color-brand-blue)" : "var(--color-neutral-150)"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Saving: Keepo's loading (the Button's), not just the faded look; these sit on neutral-dark boxes, so its
     surface is the lighter gray (the Button's own is the box's color) */
  ${({ $loading }) =>
    $loading &&
    css`
      ${buttonLoadingCss}
      background-color: var(--color-neutral-base);
      border-color: var(--color-neutral-base);

      &:disabled {
        cursor: progress;
        opacity: 1;
      }
    `}
`;

/* Under a block: a short explanation */
export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--color-neutral-150);

  &.warning {
    color: #ffb066;
  }
`;

/* The checkout's "Registrar" button with the publish button's fire ring around it (off while it processes) */
export const FireCta = styled.div`
  ${fireRingWrapper}

  .cta {
    box-shadow: none;
  }

  &:has(.cta:disabled) {
    .fire-ring,
    .fire-halo {
      opacity: 0;
    }
  }
`;

/* Loading placeholders (My domains): the shared pulsing block, in the sizes of this screen */
export const Skeleton = styled(BaseSkeleton)`
  &.title {
    width: 220px;
    height: 36px;
  }

  /* The card's thumbnail */
  &.icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 12px;

    @media ${MOBILE} {
      width: 40px;
      height: 40px;
    }
  }

  &.status {
    width: 70px;
    height: 12px;
  }

  /* A list row's small icon (36px) */
  &.row-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  /* A section's small uppercase label, and a card (the domain page) */
  &.label {
    width: 90px;
    height: 10px;
  }

  &.block {
    width: 100%;
    height: 64px;
    border-radius: 1rem;

    &.tall {
      height: 88px;
    }
  }

  &.name {
    width: 55%;
    max-width: 220px;
    height: 16px;
  }

  &.meta {
    width: 75%;
    max-width: 300px;
    height: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export * from "./mine";

export * from "./search";
